import { Box } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridFilterModel,
  GridPinnedColumnFields,
  GridRowParams,
  GridRowId,
  GridRowSelectionModel,
  GridApiPro,
} from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { InventoryV3Row } from './buildInventoryV3Rows'
import { InventoryV3Footer, InventoryV3Totals, InventoryV3ViewMode } from './InventoryV3Footer'
import { InventoryV3PhotosDetail } from './InventoryV3PhotosDetail'
import { DEFAULT_V3_GRID_STATE, INVENTORY_V3_SINGLE_SELECT_HEADER_FILTER_CLASS } from './inventoryV3DefaultTemplate'
import { useInventoryV3ColumnDragScroll } from './useInventoryV3ColumnDragScroll'

const ACTION_FIELDS = new Set(['photos', 'documents', 'timeline', 'edit', '__check__', '__detail_panel_toggle__'])
const TOGGLE_FIELDS = new Set([
  'profit_realized',
  'paid_for',
  'ready_for_sale',
  'funded',
  'show_on_website',
  'favorite',
])

interface InventoryV3GridProps {
  apiRef: React.RefObject<GridApiPro>
  rows: InventoryV3Row[]
  columns: GridColDef<InventoryV3Row>[]
  canModify: boolean
  isAdmin: boolean
  processRowUpdate: (newRow: InventoryV3Row, oldRow: InventoryV3Row) => Promise<InventoryV3Row>
  onProcessRowUpdateError: (error: Error) => void
  rowSelectionModel: GridRowSelectionModel
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void
  expandedRowIds: GridRowId[]
  onExpandedRowIdsChange: (ids: GridRowId[]) => void
  viewMode: InventoryV3ViewMode
  onChangeViewMode: (mode: InventoryV3ViewMode) => void
  totals: InventoryV3Totals
  selectionActive?: boolean
  canViewPurchasePrice: boolean
  canViewProfit: boolean
  filterModel: GridFilterModel
  onFilterModelChange: (model: GridFilterModel) => void
  columnVisibilityModel: GridColumnVisibilityModel
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void
  pinnedColumns: GridPinnedColumnFields
  onPinnedColumnsChange: (model: GridPinnedColumnFields) => void
  onColumnWidthChange: (field: string, width: number) => void
  onColumnOrderChange: (fields: string[]) => void
  fontSize: number
  rowHeight: number
  onZoomIn: () => void
  onZoomOut: () => void
}

export const InventoryV3Grid = ({
  apiRef,
  rows,
  columns,
  canModify,
  isAdmin,
  processRowUpdate,
  onProcessRowUpdateError,
  rowSelectionModel,
  onRowSelectionModelChange,
  expandedRowIds,
  onExpandedRowIdsChange,
  viewMode,
  onChangeViewMode,
  totals,
  selectionActive = false,
  canViewPurchasePrice,
  canViewProfit,
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  pinnedColumns,
  onPinnedColumnsChange,
  onColumnWidthChange,
  onColumnOrderChange,
  fontSize,
  rowHeight,
  onZoomIn,
  onZoomOut,
}: InventoryV3GridProps) => {
  useInventoryV3ColumnDragScroll(apiRef)

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<InventoryV3Row>) => (
      <InventoryV3PhotosDetail vehicle={row.vehicle} canModify={canModify} />
    ),
    [canModify],
  )

  const handleCellClick = useCallback<GridEventListener<'cellClick'>>(
    params => {
      if (ACTION_FIELDS.has(String(params.field))) return
      if (TOGGLE_FIELDS.has(String(params.field))) return
      if (!params.isEditable) return
      params.api.startCellEditMode({ id: params.id, field: params.field })
    },
    [],
  )

  const gridContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = gridContainerRef.current
    if (!node) return

    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      if (event.deltaY > 0) onZoomOut()
      else onZoomIn()
    }

    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [onZoomIn, onZoomOut])

  // Visibility, pinning, widths, and order are controlled props — keep them out of initialState.
  const columnHeaderHeight = Math.max(40, rowHeight)
  const headerFilterHeight = Math.max(48, rowHeight + 16)

  const initialState = useMemo(
    () => ({
      sorting: DEFAULT_V3_GRID_STATE.sorting,
    }),
    [],
  )

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box ref={gridContainerRef} sx={{ flex: 1, minHeight: 0 }}>
          <DataGridPro<InventoryV3Row>
            apiRef={apiRef as React.MutableRefObject<GridApiPro>}
            rows={rows}
            columns={columns}
            getRowId={row => row.id}
            getRowHeight={() => rowHeight}
            columnHeaderHeight={columnHeaderHeight}
            checkboxSelection
            disableRowSelectionOnClick
            hideFooter
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={onProcessRowUpdateError}
            onCellClick={handleCellClick}
            onCellEditStop={(params, event) => {
              if (params.reason === 'escapeKeyDown') return
              const cell = params.api.getCellElement(params.id, params.field)
              if (cell?.classList.contains('Mui-error')) {
                event.defaultMuiPrevented = true
              }
            }}
            isCellEditable={params => {
              if (params.field === 'stock_number') return false
              if (params.field === 'sold_date') {
                const isVehicleSold = params.row.status?.toLowerCase() === 'sold'
                return canModify && (!isVehicleSold || isAdmin)
              }
              return params.colDef.editable === true
            }}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={onPinnedColumnsChange}
            onColumnWidthChange={params => onColumnWidthChange(String(params.colDef.field), params.width)}
            onColumnOrderChange={() => {
              onColumnOrderChange(apiRef.current?.exportState?.().columns?.orderedFields ?? [])
            }}
            filterDebounceMs={300}
            headerFilters
            headerFilterHeight={headerFilterHeight}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={() => 320}
            detailPanelExpandedRowIds={expandedRowIds}
            onDetailPanelExpandedRowIdsChange={onExpandedRowIdsChange}
            initialState={initialState}
            density='compact'
            slotProps={{
              baseTextField: {
                label: '',
                placeholder: 'Filter',
                InputLabelProps: { sx: { display: 'none' } },
                size: 'small',
              },
              baseSelect: {
                MenuProps: {
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  MenuListProps: { dense: true },
                  PaperProps: {
                    elevation: 2,
                    sx: {
                      maxHeight: 240,
                      borderRadius: 0,
                      '& .MuiMenuItem-root': {
                        fontSize,
                        minHeight: 28,
                        lineHeight: 1.25,
                        py: 0.25,
                      },
                    },
                  },
                },
              },
            }}
            sx={{
              height: '100%',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              // Built-in drag scroll zones are only 20px and event-driven; we use a custom rAF scroller.
              '& .MuiDataGrid-scrollArea': {
                display: 'none',
              },
              '& .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                fontSize,
                py: 0,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize,
              },
              '& .MuiDataGrid-cell .MuiTypography-root': {
                fontSize: 'inherit',
              },
              '& .MuiDataGrid-cell .MuiChip-root': {
                fontSize: 'inherit',
                height: 'auto',
              },
              '& .MuiDataGrid-cell .MuiChip-label': {
                fontSize: 'inherit',
                lineHeight: 1.25,
                py: '0.15em',
              },
              '& .MuiDataGrid-columnHeader .MuiInputBase-input': {
                fontSize,
              },
              '& .MuiDataGrid-headerFilterRow .MuiDataGrid-columnHeader': {
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                py: 0.5,
                boxSizing: 'border-box',
                '& > .MuiAutocomplete-root, & > .MuiFormControl-root': {
                  width: '100%',
                  minWidth: 0,
                  flex: 1,
                },
              },
              // overflow:hidden on the filter row breaks sticky pins; keep cells clipped instead.
              '& .MuiDataGrid-headerFilterRow .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-headerFilterRow .MuiDataGrid-columnHeader--pinnedRight': {
                position: 'sticky',
                zIndex: 5,
                backgroundColor: 'var(--DataGrid-pinnedBackground)',
              },
              '& .MuiDataGrid-headerFilterRow .MuiInputLabel-root': {
                display: 'none',
              },
              '& .MuiDataGrid-headerFilterRow .MuiInput-root': {
                marginTop: 0,
              },
              // MUI hides empty number/date input text so the operator label shows;
              // we hide that label, so restore color so the Filter placeholder is visible.
              '& .MuiDataGrid-headerFilterRow input[value=""]:not(:focus)': {
                color: 'text.primary',
              },
              '& .MuiDataGrid-headerFilterRow input[type="number"]': {
                MozAppearance: 'textfield',
                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              },
              '& .MuiDataGrid-headerFilterRow .MuiInputBase-root': {
                fontSize,
                width: '100%',
                height: '100%',
                maxHeight: '100%',
              },
              [`& .MuiDataGrid-headerFilterRow .${INVENTORY_V3_SINGLE_SELECT_HEADER_FILTER_CLASS}`]: {
                '& > div:first-of-type': {
                  width: '100%',
                  alignItems: 'center',
                },
                '& .MuiFormControl-root': {
                  width: '100%',
                },
                // Status multi-select — fill the column (operator menu is not rendered).
                '& .MuiAutocomplete-root, & .MuiFormControl-root': {
                  width: '100%',
                  minWidth: 0,
                  flex: 1,
                },
                // Hide the header filter operator menu (not Autocomplete popup indicators).
                '& > button[aria-haspopup="true"]': {
                  display: 'none',
                },
              },
              '& .MuiDataGrid-headerFilterRow .MuiInputBase-input, & .MuiDataGrid-headerFilterRow .MuiSelect-select': {
                fontSize,
                py: 0,
                boxSizing: 'border-box',
              },
              '& .MuiDataGrid-cell--editing': {
                p: 0,
                '& .MuiInputBase-root': {
                  fontSize: 'inherit',
                  height: '100%',
                },
                '& .MuiDataGrid-editInputCell': {
                  fontSize: 'inherit',
                  height: '100%',
                  '& input': {
                    fontSize: 'inherit',
                    height: '100%',
                    py: 0,
                    px: 1,
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 0,
                },
                '& .MuiInput-underline:before, & .MuiInput-underline:after': {
                  display: 'none',
                },
              },
              '& .MuiDataGrid-cell--editing.Mui-error .MuiInputBase-root': {
                color: 'error.main',
              },
            }}
          />
      </Box>
      <InventoryV3Footer
        viewMode={viewMode}
        onChangeViewMode={onChangeViewMode}
        totals={totals}
        selectionActive={selectionActive}
        canViewPurchasePrice={canViewPurchasePrice}
        canViewProfit={canViewProfit}
      />
    </Box>
  )
}
